import { PostComment } from "@/src/models/post.model";
import React from "react";
import { useTranslation } from "react-i18next";
import {
  ActivityIndicator,
  RefreshControl,
  ScrollView,
  Text,
  View,
} from "react-native";
import CommentSkeleton from "../loading-skeletons/comment-loading-skeleton";
import { CommentItem } from "./comment-item";
type CommentsListProps = Readonly<{
  comments: PostComment[];
  isLoading: boolean;
  isError?: boolean;
  onRefresh: () => void;
  onReply?: (commentId: string, body: string) => void;
  onLoadMore?: () => void;
  hasNextPage?: boolean;
  isFetchingNextPage?: boolean;
}>;

const CommentsList = ({
  comments,
  isLoading,
  isError,
  onRefresh,
  onReply,
  onLoadMore,
  hasNextPage,
  isFetchingNextPage,
}: CommentsListProps) => {
  const { t } = useTranslation();

  const topLevelComments = comments.filter(
    (comment) => !comment.parentCommentId,
  );
  const replies = comments.filter((comment) => !!comment.parentCommentId);

  const handleScrollEnd = ({ nativeEvent }: any) => {
    const { layoutMeasurement, contentOffset, contentSize } = nativeEvent;
    const isCloseToBottom =
      layoutMeasurement.height + contentOffset.y >= contentSize.height - 20;
    if (isCloseToBottom && hasNextPage && !isFetchingNextPage) {
      onLoadMore?.();
    }
  };

  const renderComment = (
    comment: PostComment,
    level: number,
    parentAuthor?: string,
  ) => (
    <View key={comment.ItemId}>
      <CommentItem
        comment={comment}
        level={level}
        parentAuthor={parentAuthor}
        onReply={onReply}
      />
      {replies
        .filter((reply) => reply.parentCommentId === comment.ItemId)
        .map((reply) =>
          renderComment(
            reply,
            Math.min(level + 1, 2),
            comment.authorSnapshot.firstName ?? "",
          ),
        )}
    </View>
  );

  return (
    <View className="px-4 pt-3 max-h-[300px]">
      <Text className="font-medium mb-2 text-onyx">
        {t("LABEL_COMMENTS")} ({comments.length})
      </Text>
      <ScrollView
        nestedScrollEnabled
        showsVerticalScrollIndicator={false}
        onScroll={handleScrollEnd}
        scrollEventThrottle={16}
        refreshControl={
          <RefreshControl refreshing={isLoading} onRefresh={onRefresh} />
        }
        contentContainerStyle={{
          flexGrow: 1,
          padding: 4,
          justifyContent:
            comments.length === 0 && !isLoading ? "center" : "flex-start",
        }}
      >
        {isLoading && comments.length === 0 && (
          <>
            {Array.from({ length: 4 }).map((_, index) => (
              <CommentSkeleton key={index} />
            ))}
          </>
        )}
        {isError && !isLoading && (
          <View className="items-center my-4">
            <Text className="text-red-500 text-sm">
              {t("ERROR_FAILED_TO_LOAD_COMMENTS")}
            </Text>
          </View>
        )}
        {!isLoading && !isError && comments.length === 0 && (
          <View className="items-center pb-9 my-2">
            <Text className="text-sm text-slate">{t("LABEL_NO_COMMENTS_YET")}</Text>
          </View>
        )}
        {!isLoading &&
          topLevelComments.map((comment) => renderComment(comment, 0))}
        {isFetchingNextPage && (
          <View className="items-center py-2">
            <ActivityIndicator size="small" />
          </View>
        )}
      </ScrollView>
    </View>
  );
};

export default CommentsList;
